import { Badge } from '@soteria/design-system-ve';

import { COMPOSITION_CONSTANTS } from './compositionUtils';
import * as styles from './styles';
import type { FilterBadgeSectionProps } from './types';

/**
 * FilterBadgeSection component - Extracted from CommunityAuditDetailHeaderSection
 * Handles rendering of filter badge groups with consistent styling and limits
 */
export function FilterBadgeSection({ session }: FilterBadgeSectionProps) {
  const filters = session.sampleParameters?.filters;
  const { MAX_FILTER_BADGES } = COMPOSITION_CONSTANTS;

  // If no filters are available, don't render anything
  if (!filters) {
    return null;
  }

  const renderStringBadgeGroup = (options: { label: string; items: string[] | undefined; maxDisplay: number }) => {
    const { label, items, maxDisplay } = options;

    const getHiddenItemsTooltip = () => {
      if (!items || items.length <= maxDisplay) return undefined;
      const hiddenItems = items.slice(maxDisplay);

      return hiddenItems.join(', ');
    };

    return (
      <div className={styles.badgeGroup}>
        <span className={styles.badgeLabel}>{label}:</span>
        <div className={styles.compactBadgeList}>
          {items && items.length > 0 ? (
            <>
              {items.slice(0, maxDisplay).map(item => (
                <Badge key={item} text={item} />
              ))}
              {items.length > maxDisplay && <Badge text={`+${items.length - maxDisplay} more`} title={getHiddenItemsTooltip()} />}
            </>
          ) : (
            <span className={styles.emptyBadgeText}>None</span>
          )}
        </div>
      </div>
    );
  };

  const renderSeverityBadgeGroup = () => {
    const getHiddenSeveritiesTooltip = () => {
      if (!filters.labelSeverities || filters.labelSeverities.length <= MAX_FILTER_BADGES.severities) return undefined;
      const hiddenSeverities = filters.labelSeverities.slice(MAX_FILTER_BADGES.severities);

      return hiddenSeverities.map((severity: number) => `Level ${severity}`).join(', ');
    };

    return (
      <div className={styles.badgeGroup}>
        <span className={styles.badgeLabel}>Severities:</span>
        <div className={styles.compactBadgeList}>
          {filters.labelSeverities && filters.labelSeverities.length > 0 ? (
            <>
              {filters.labelSeverities.slice(0, MAX_FILTER_BADGES.severities).map((severity: number) => (
                <Badge key={severity} text={`Level ${severity}`} />
              ))}
              {filters.labelSeverities.length > MAX_FILTER_BADGES.severities && (
                <Badge text={`+${filters.labelSeverities.length - MAX_FILTER_BADGES.severities} more`} title={getHiddenSeveritiesTooltip()} />
              )}
            </>
          ) : (
            <span className={styles.emptyBadgeText}>None</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className={styles.appliedFiltersHeader}>Applied Filters</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
        {renderStringBadgeGroup({
          label: 'Languages',
          items: filters.languages,
          maxDisplay: MAX_FILTER_BADGES.languages,
        })}
        {renderStringBadgeGroup({
          label: 'Topics',
          items: filters.topics,
          maxDisplay: MAX_FILTER_BADGES.topics,
        })}
        {renderStringBadgeGroup({
          label: 'Labels',
          items: filters.labels,
          maxDisplay: MAX_FILTER_BADGES.labels,
        })}
        {renderSeverityBadgeGroup()}
        {renderStringBadgeGroup({
          label: 'Content Types',
          items: filters.contentTypes,
          maxDisplay: MAX_FILTER_BADGES.contentTypes,
        })}
      </div>
    </div>
  );
}
